import React from "react";
import { PrimaryButton } from "../../design-system/components/buttons";
import { typography, surfaces, borders, textColors } from "../../design-system/tokens";
import {
  LIFECYCLE_ACTIONS,
  deriveLifecycleState,
  getAvailableActions,
} from "../utils/subscriptionLifecycle";

/**
 * Action card rendered below the available plans. Driven entirely by the
 * lifecycle state machine — every action shown here is one entry in
 * `getAvailableActions(state)`.
 *
 * Responsibilities are deliberately narrow:
 *   - Decide which rows to render via the state machine.
 *   - Render each row uniformly (title + description + button + per-action error).
 *   - Forward clicks to the parent's callbacks.
 *
 * Anything else (modal flows, schedule API calls, store updates) belongs in
 * the parent. This keeps the component pure-ish and easy to test in isolation.
 */

const ActionRow = ({ title, description, button, error, isLast }) => (
  <div
    className={`px-4 sm:px-6 py-4 sm:py-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 ${
      isLast ? "" : `border-b ${borders.glass}`
    }`}
  >
    <div className="space-y-1">
      <h3 className={`${typography.h5} ${textColors.primary}`}>{title}</h3>
      <p className={`${typography.caption} ${textColors.tertiary}`}>{description}</p>
      {error ? (
        <p className={`${typography.caption} text-red-600 dark:text-red-400`}>{error}</p>
      ) : null}
    </div>
    <div className="self-start sm:self-auto">{button}</div>
  </div>
);

const InlineActionLink = ({ children, onClick, disabled, tone = "primary" }) => {
  const toneClasses =
    tone === "danger"
      ? "text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300"
      : "text-blue-600 dark:text-blue-500 hover:text-blue-700 dark:hover:text-blue-400";
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`${typography.bodySmall} ${toneClasses} underline text-left disabled:opacity-60 disabled:cursor-not-allowed`}
    >
      {children}
    </button>
  );
};

const SubscriptionLifecycleActions = ({
  subscription,
  upcomingSubscription,
  currentPlan,

  onCancelSubscription,
  cancelSubscriptionLoading = false,

  onResumeRenewal,
  resumeRenewalLoading = false,
  resumeRenewalError = null,

  onCancelScheduledChange,
  cancelScheduledChangeLoading = false,
  cancelScheduledChangeError = null,
}) => {
  const state = deriveLifecycleState({ subscription, upcomingSubscription });
  const actions = getAvailableActions(state);
  if (!actions.length) return null;

  const currentPlanLabel =
    currentPlan?.display_name || subscription?.plan_display_name || "current plan";
  const upcomingPlanLabel =
    upcomingSubscription?.plan_display_name ||
    (upcomingSubscription?.plan ? upcomingSubscription.plan.toUpperCase() : "scheduled plan");

  const renderRow = (action, isLast) => {
    if (action === LIFECYCLE_ACTIONS.RESUME_RENEWAL) {
      return (
        <ActionRow
          key={action}
          isLast={isLast}
          title="Renewal is currently turned off"
          description="Your subscription will end at the end of the current billing period unless you resume renewal."
          error={resumeRenewalError}
          button={
            <PrimaryButton onClick={onResumeRenewal} loading={resumeRenewalLoading}>
              Resume renewal
            </PrimaryButton>
          }
        />
      );
    }

    if (action === LIFECYCLE_ACTIONS.CANCEL_SCHEDULED_CHANGE) {
      return (
        <ActionRow
          key={action}
          isLast={isLast}
          title={`Cancel scheduled switch to ${upcomingPlanLabel}`}
          description={`Stay on your ${currentPlanLabel} plan and drop the upcoming switch to ${upcomingPlanLabel}.`}
          error={cancelScheduledChangeError}
          button={
            <InlineActionLink
              onClick={onCancelScheduledChange}
              disabled={cancelScheduledChangeLoading}
              tone="primary"
            >
              {cancelScheduledChangeLoading
                ? "Keeping your current plan…"
                : `Keep ${currentPlanLabel} instead`}
            </InlineActionLink>
          }
        />
      );
    }

    if (action === LIFECYCLE_ACTIONS.CANCEL_SUBSCRIPTION) {
      // Description shifts based on whether a switch is also pending — but the
      // *behavior* is the same single click. The rendering split lives here
      // (one action, two copy variants) rather than in the state machine.
      const description =
        upcomingSubscription
          ? `Cancel both your ${currentPlanLabel} subscription and the scheduled switch to ${upcomingPlanLabel}. You'll keep access until the end of your current billing period.`
          : "You'll keep access until the end of your current billing period.";
      return (
        <ActionRow
          key={action}
          isLast={isLast}
          title="Cancel subscription"
          description={description}
          button={
            <InlineActionLink
              onClick={onCancelSubscription}
              disabled={cancelSubscriptionLoading}
              tone="primary"
            >
              Cancel subscription
            </InlineActionLink>
          }
        />
      );
    }

    return null;
  };

  return (
    <section
      aria-label="Manage subscription"
      className={`rounded-2xl shadow-sm ${surfaces.glassCard} border ${borders.glass} overflow-hidden`}
    >
      {actions.map((action, index) => renderRow(action, index === actions.length - 1))}
    </section>
  );
};

export default SubscriptionLifecycleActions;
